import { Dispatch, SetStateAction, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog';
import * as Toast from '@/components/ui/toast/Toast';
import { useScout } from '@/hooks/useScout';
import { createBursts } from '@/api/developer';
import { Checkbox } from '@/components/shadcn/checkbox';
import { useMapCredits } from '@/hooks/useMapCredits';
import { Burst } from '@/types/location';
import { useUser } from '@/hooks/useUser';

interface Props {
  open: boolean;
  setBurstModalOptions: Dispatch<
    SetStateAction<{
      isOpen: boolean;
      locationsIds: string[];
      successCallback: () => void;
    }>
  >;
  burstAmount: number;
  locationsIds: string[];
  successCallback: () => void;
}

export default function BurstModal({
  open,
  setBurstModalOptions,
  burstAmount,
  locationsIds,
  successCallback,
}: Props) {
  const [selected, setSelected] = useState(false);

  const { setLocations, setForceMapUpdate } = useScout();
  const { setMapCredits } = useMapCredits();
  const { user } = useUser();

  const burstLocations = async (
    ids: string[],
    successCallback: () => void,
    currentOrganization: string | null,
  ) => {
    const response = await createBursts(ids, currentOrganization);

    if ('error' in response) {
      console.error(response.error);
      toast.custom(t => (
        <Toast.Default
          title="Try Again"
          description="We were unable to create bursts, please try again."
          buttons={[
            {
              label: 'Dismiss',
              onClick: () => toast.dismiss(t),
              variant: 'secondary',
              size: 'fw',
            },
          ]}
        />
      ));
      return;
    }

    if (response.bursts.length > 0) {
      toast.custom(t => (
        <Toast.Default
          title="Burst Activated"
          description="Bursts are now active and contributors have been notified. Check back later for updates."
          buttons={[
            {
              label: 'Dismiss',
              onClick: () => {
                toast.dismiss(t);
              },
              variant: 'secondary',
              size: 'fw',
            },
          ]}
        />
      ));

      const burstsByLocation = response.bursts.reduce(
        (acc: Record<string, Burst[]>, burst: Burst) => {
          const locationId = String(burst.trackedLocation);
          if (!acc[locationId]) {
            acc[locationId] = [];
          }
          acc[locationId].push(burst);
          return acc;
        },
        {} as Record<string, Burst[]>,
      );

      setLocations(prevState => {
        return prevState.map(location => {
          const locationId = String(location._id);
          return {
            ...location,
            bursts: burstsByLocation[locationId] ?? location.bursts,
          };
        });
      });

      setMapCredits(response.creditsRemaining);
      successCallback && successCallback();

      setBurstModalOptions({
        isOpen: false,
        locationsIds: [],
        successCallback: () => {},
      });

      setForceMapUpdate(true);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent className="max-w-[280px]">
        <DialogHeader>
          <DialogTitle>Burst Location</DialogTitle>
          <DialogDescription>
            <div className="flex flex-col gap-4">
              <div>
                Need fresh imagery at this location? Add a Burst and we’ll
                handle the rest.
              </div>
              <div
                data-selected={selected}
                className="rounded border p-2 data-[selected=true]:border-harvestGoldWarning-600">
                <div className="flex">
                  <div className="flex items-center justify-center">
                    <Checkbox
                      onClick={() => {
                        setSelected(prevState => !prevState);
                      }}
                      checked={selected}
                      className="mr-3 border-neutral-700 data-[state=checked]:border-harvestGoldWarning-600 data-[state=checked]:bg-harvestGoldWarning-600 data-[state=checked]:text-neutral-900"
                    />
                  </div>
                  <div className="flex flex-col text-left">
                    <div className="font-bold">
                      125 Map Credits per location
                    </div>
                    <div className="text-xs">Active for 7 days</div>
                  </div>
                </div>
              </div>
              <div>{`${locationsIds.length} location${locationsIds.length > 1 ? 's' : ''} = ${burstAmount} Map Credits`}</div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setBurstModalOptions(prevState => ({
                    ...prevState,
                    isOpen: false,
                  }));
                  setSelected(false);
                }}>
                Cancel
              </Button>
              <Button
                disabled={!selected || !user}
                variant="burst"
                onClick={() => {
                  if (user) {
                    burstLocations(
                      locationsIds,
                      successCallback,
                      user.currentOrganization,
                    );
                  }
                  setSelected(false);
                }}>
                Burst
              </Button>
            </div>
            <div className="text-center text-xs">
              Locations cannot be removed during the duration of the Burst. Map
              Credits are refunded if fresh imagery isn’t captured during the
              duration of the Burst.
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
